/**
 * Request an idle callback or fallback to setTimeout
 * @returns {function} The requestIdleCallback function
 */
export const requestIdleCallback = typeof window.requestIdleCallback == "function" ? window.requestIdleCallback : setTimeout;

/**
 * Executes a callback in a separate task after the next frame.
 * Using to defer non-critical tasks until after the interaction is complete.
 * @see https://web.dev/articles/optimize-inp#yield_to_allow_rendering_work_to_occur_sooner
 * @param {() => any} callback - The callback to execute
 */
export const requestYieldCallback = (callback) => {
  requestAnimationFrame(() => {
    setTimeout(callback, 0);
  });
};

/**
 * Tells if we are on a low power device based on the number of CPU cores and RAM
 * @returns {boolean} True if the device is a low power device, false otherwise
 */
export function isLowPowerDevice() {
  return Number(navigator.hardwareConcurrency) <= 2 || Number(navigator.deviceMemory) <= 2;
}

/**
 * Check if the browser supports View Transitions API
 * @returns {boolean} True if the browser supports View Transitions API, false otherwise
 */
export function supportsViewTransitions() {
  // return typeof document.startViewTransition === "function";
  return false;
}

/**
 * Detects if the browser is Chrome/Chromium-based
 * @returns {boolean} True if Chrome/Chromium, false otherwise
 */
function isChrome() {
  // Check for Chrome user agent
  const userAgent = navigator.userAgent.toLowerCase();
  const isChromeUA = userAgent.includes("chrome") && !userAgent.includes("edg");
  // Also check for Chrome-specific APIs
  // @ts-ignore - chrome property exists in Chrome browsers
  const isChromeAPI = typeof window.chrome !== "undefined" && window.chrome?.runtime;
  return isChromeUA || isChromeAPI;
}

/**
 * The current view transition
 * @type {{ current: Promise<void> | undefined }}
 */
export const viewTransition = {
  current: undefined,
};

/**
 * Functions to run when a view transition of a given type is started
 * @type {{ [key: string]: () => Promise<(() => void) | undefined> }}
 */
const viewTransitionTypes = {
  "product-grid": async () => {
    const grid = document.querySelector(".product-grid");
    const productCards = /** @type {HTMLElement[]} */ ([...document.querySelectorAll(".product-grid .product-grid__item")]);

    if (!grid || !productCards.length) return;

    // Skip card hiding for Chrome to prevent blank screen issue
    const isChromeBrowser = isChrome();
    if (isChromeBrowser) {
      // For Chrome, don't hide cards - just set view-transition-name for animated cards
      await new Promise((resolve) =>
        requestIdleCallback(() => {
          const cardsToAnimate = getCardsToAnimate(grid, productCards);
          productCards.forEach((card, index) => {
            if (index < cardsToAnimate) {
              card.style.setProperty("view-transition-name", `product-card-${card.dataset.productId}`);
            }
            // Don't hide other cards in Chrome to prevent blank screen
          });
          resolve(null);
        })
      );

      return () =>
        productCards.forEach((card) => {
          card.style.removeProperty("view-transition-name");
        });
    }

    // For other browsers (Firefox, Safari, etc.), use the original approach
    await new Promise((resolve) =>
      requestIdleCallback(() => {
        const cardsToAnimate = getCardsToAnimate(grid, productCards);

        productCards.forEach((card, index) => {
          console.log("cardsToAnimate---------->>>>>", cardsToAnimate, index);
          if (index < cardsToAnimate) {
            card.style.setProperty("view-transition-name", `product-card-${card.dataset.productId}`);
          } else {
            card.style.setProperty("content-visibility", "hidden");
          }
        });

        resolve(null);
      })
    );

    return () =>
      productCards.forEach((card) => {
        card.style.removeProperty("view-transition-name");
        card.style.removeProperty("content-visibility");
      });
  },
};

/**
 * Starts a view transition
 * @param {() => void} callback The callback to call when the view transition starts
 * @param {string[]} [types] The types of view transition to use
 * @returns {Promise<void>} A promise that resolves when the view transition finishes
 */
export function startViewTransition(callback, types) {
  // eslint-disable-next-line no-async-promise-executor
  return new Promise(async (resolve) => {
    // Disable view transitions for Chrome to prevent blank screen issue
    const isChromeBrowser = isChrome();

    // Check if View Transitions API is supported and not Chrome
    if (supportsViewTransitions() && !prefersReducedMotion() && !isChromeBrowser) {
      let cleanupFunctions = [];

      if (types) {
        for (const type of types) {
          if (viewTransitionTypes[type]) {
            const cleanupFunction = await viewTransitionTypes[type]();
            if (cleanupFunction) cleanupFunctions.push(cleanupFunction);
          }
        }
      }

      const transition = document.startViewTransition(callback);

      if (!viewTransition.current) {
        viewTransition.current = transition.finished;
      }

      if (types) types.forEach((type) => transition.types.add(type));

      transition.finished.then(() => {
        viewTransition.current = undefined;
        cleanupFunctions.forEach((cleanupFunction) => cleanupFunction());
        resolve();
      });

      return;
    }

    // Fallback for Chrome and browsers that don't support this API yet
    // For Chrome, directly call callback without view transition to prevent blank screen
    await callback();
    resolve();
  });
}

/**
 * @typedef {{ [key: string]: string | undefined }} Headers
 */

/**
 * @typedef {Object} FetchConfig
 * @property {string} method
 * @property {Headers} headers
 * @property {string | FormData | undefined} [body]
 */

/**
 * Creates a fetch configuration object
 * @param {string} [type] The type of response to expect
 * @param {Object} [config] The config of the request
 * @param {FetchConfig['body']} [config.body] The body of the request
 * @param {FetchConfig['headers']} [config.headers] The headers of the request
 * @returns {RequestInit} The fetch configuration object
 */
export function fetchConfig(type = "json", config = {}) {
  /** @type {Headers} */
  const headers = { "Content-Type": "application/json", Accept: `application/${type}`, ...config.headers };

  if (type === "javascript") {
    headers["X-Requested-With"] = "XMLHttpRequest";
    delete headers["Content-Type"];
  }

  return {
    method: "POST",
    headers: /** @type {HeadersInit} */ (headers),
    body: config.body,
  };
}

/**
 * Creates a debounced function that delays calling the provided function (fn)
 * until after wait milliseconds have elapsed since the last time
 * the debounced function was invoked. The returned function has a .cancel()
 * method to cancel any pending calls.
 *
 * @template {(...args: any[]) => any} T
 * @param {T} fn The function to debounce
 * @param {number} wait The time (in milliseconds) to wait before calling fn
 * @returns {T & { cancel(): void }} A debounced version of fn with a .cancel() method
 */
export function debounce(fn, wait) {
  /** @type {number | undefined} */
  let timeout;

  /** @param {...any} args */
  function debounced(...args) {
    clearTimeout(timeout);
    timeout = setTimeout(() => fn.apply(this, args), wait);
  }

  // Add the .cancel method:
  debounced.cancel = () => {
    clearTimeout(timeout);
  };

  return /** @type {T & { cancel(): void }} */ (debounced);
}

/**
 * Creates a throttled function that calls the provided function (fn) at most once per every wait milliseconds
 *
 * @template {(...args: any[]) => any} T
 * @param {T} fn The function to throttle
 * @param {number} delay The time (in milliseconds) to wait before calling fn
 * @returns {T & { cancel(): void }} A throttled version of fn with a .cancel() method
 */
export function throttle(fn, delay) {
  let lastCall = 0;

  /** @param {...any} args */
  function throttled(...args) {
    const now = performance.now();
    // If the time since the last call exceeds the delay, execute the callback
    if (now - lastCall >= delay) {
      lastCall = now;
      fn.apply(this, args);
    }
  }

  throttled.cancel = () => {
    lastCall = performance.now();
  };

  return /** @type {T & { cancel(): void }} */ (throttled);
}

/**
 * A media query for reduced motion
 * @type {MediaQueryList}
 */
const reducedMotion = matchMedia("(prefers-reduced-motion: reduce)");

/**
 * Check if the user prefers reduced motion
 * @returns {boolean} True if the user prefers reduced motion, false otherwise
 */
export function prefersReducedMotion() {
  return reducedMotion.matches;
}

/**
 * Normalize a string
 * @param {string} str The string to normalize
 * @returns {string} The normalized string
 */
export function normalizeString(str) {
  return str
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase();
}

/**
 * Format a money value
 * @param {string} value The value to format
 * @returns {string} The formatted value
 */
export function formatMoney(value) {
  let valueWithNoSpaces = value.replace(" ", "");
  if (valueWithNoSpaces.indexOf(",") === -1) return valueWithNoSpaces;
  if (valueWithNoSpaces.indexOf(",") < valueWithNoSpaces.indexOf(".")) return valueWithNoSpaces.replace(",", "");
  if (valueWithNoSpaces.indexOf(".") < valueWithNoSpaces.indexOf(",")) return valueWithNoSpaces.replace(".", "").replace(",", ".");
  if (valueWithNoSpaces.indexOf(",") !== -1) return valueWithNoSpaces.replace(",", ".");

  return valueWithNoSpaces;
}

/**
 * Check if the document is ready/loaded and call the callback when it is.
 * @param {() => void} callback The function to call when the document is ready.
 */
export function onDocumentLoaded(callback) {
  if (document.readyState === "complete") {
    callback();
  } else {
    window.addEventListener("load", callback);
  }
}

/**
 * Check if the DOM is ready and call the callback when it is.
 * This fires when the DOM is fully parsed but before all resources are loaded.
 * @param {() => void} callback The function to call when the DOM is ready.
 */
export function onDocumentReady(callback) {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", callback);
  } else {
    callback();
  }
}

/**
 * Wait for all animations to finish before calling the callback.
 * @param {Element | Element[]} elements The element(s) whose animations to wait for.
 * @param {() => void} [callback] The function to call when all animations are finished.
 * @param {Object} [options] The options to pass to `Element.getAnimations`.
 * @returns {Promise<void>} A promise that resolves when all animations are finished.
 */
export function onAnimationEnd(elements, callback, options = { subtree: true }) {
  const animations = Array.isArray(elements) ? elements.flatMap((element) => element.getAnimations(options)) : elements.getAnimations(options);
  const animationPromises = animations.reduce((acc, animation) => {
    // Ignore ViewTimeline animations
    if (animation.timeline instanceof DocumentTimeline) {
      acc.push(animation.finished);
    }

    return acc;
  }, /** @type {Promise<Animation>[]} */ ([]));

  return Promise.allSettled(animationPromises).then(callback);
}

/**
 * Check if the click is outside the element.
 * @param {MouseEvent} event The mouse event.
 * @param {Element} element The element to check.
 * @returns {boolean} True if the click is outside the element, false otherwise.
 */
export function isClickedOutside(event, element) {
  if (event.target instanceof HTMLDialogElement || !(event.target instanceof Element)) {
    return !isPointWithinElement(event.clientX, event.clientY, element);
  }

  return !element.contains(event.target);
}

/**
 * Check if a point is within an element.
 * @param {number} x The x coordinate of the point.
 * @param {number} y The y coordinate of the point.
 * @param {Element} element The element to check.
 * @returns {boolean} True if the point is within the element, false otherwise.
 */
export function isPointWithinElement(x, y, element) {
  const { left, right, top, bottom } = element.getBoundingClientRect();

  return x >= left && x <= right && y >= top && y <= bottom;
}

/**
 * A media query for large screens
 * @type {MediaQueryList}
 */
export const mediaQueryLarge = matchMedia("(min-width: 750px)");

/**
 * Check if the current breakpoint is mobile
 * @returns {boolean} True if the current breakpoint is mobile, false otherwise
 */
export function isMobileBreakpoint() {
  return !mediaQueryLarge.matches;
}

/**
 * Check if the current breakpoint is desktop
 * @returns {boolean} True if the current breakpoint is desktop, false otherwise
 */
export function isDesktopBreakpoint() {
  return mediaQueryLarge.matches;
}

/**
 * Clamps a number between a minimum and maximum value.
 * @param {number} value - The input number to clamp.
 * @param {number} min - The minimum value.
 * @param {number} max - The maximum value.
 * @returns {number} The clamped value.
 */
export function clamp(value, min, max) {
  return Math.max(min, Math.min(value, max));
}

/**
 * Calculates the center point of an element along the specified axis.
 * @param {Element} element - The DOM element to find the center of.
 * @param {'x' | 'y'} [axis] - The axis ('x' or 'y') to get the center for. If not provided, returns both axes.
 * @template {('x' | 'y')} T
 * @param {T} [axis]
 * @returns {T extends ('x' | 'y') ? number : {x: number, y: number}} The center point along the axis or an object with x and y coordinates.
 */
export function center(element, axis) {
  const { left, width, top, height } = element.getBoundingClientRect();
  const point = {
    x: left + width / 2,
    y: top + height / 2,
  };

  if (axis) return /**  @type {any} */ (point[axis]);

  return /**  @type {any} */ (point);
}

/**
 * Calculates the start point of an element along the specified axis.
 * @param {Element} element - The DOM element to find the start of.
 * @param {'x' | 'y'} [axis] - The axis ('x' or 'y') to get the start for. If not provided, returns both axes.
 * @returns {number | {x: number, y: number}} The start point along the axis or an object with x and y coordinates.
 */
export function start(element, axis) {
  const { left, top } = element.getBoundingClientRect();
  const point = { x: left, y: top };

  if (axis) return /**  @type {any} */ (point[axis]);

  return /**  @type {any} */ (point);
}

/**
 * Finds the value in an array that is closest to a target value.
 * @param {number[]} values - An array of numbers.
 * @param {number} target - The target number to find the closest value to.
 * @returns {number} The value from the array closest to the target.
 */
export function closest(values, target) {
  return values.reduce(function (prev, curr) {
    return Math.abs(curr - target) < Math.abs(prev - target) ? curr : prev;
  });
}

/**
 * Prevents the default action of an event.
 * @param {Event} event - The event to prevent the default action of.
 */
export function preventDefault(event) {
  event.preventDefault();
}

/**
 * Get the visible elements within a root element.
 * @template {Element} T
 * @param {Element} root - The element within which elements should be visible.
 * @param {T[] | undefined} elements - The elements to check for visibility.
 * @param {number} [ratio=1] - The minimum percentage of the element that must be visible.
 * @param {'x' | 'y'} [axis] - Whether to only check along 'x' axis, 'y' axis, or both if undefined.
 * @returns {T[]} An array containing the visible elements.
 */
export function getVisibleElements(root, elements, ratio = 1, axis) {
  if (!elements?.length) return [];
  const rootRect = root.getBoundingClientRect();

  return elements.filter((element) => {
    const { width, height, top, right, left, bottom } = element.getBoundingClientRect();

    if (ratio < 1) {
      const intersectionLeft = Math.max(rootRect.left, left);
      const intersectionRight = Math.min(rootRect.right, right);
      const intersectionWidth = Math.max(0, intersectionRight - intersectionLeft);

      if (axis === "x") {
        return width > 0 && intersectionWidth / width >= ratio;
      }

      const intersectionTop = Math.max(rootRect.top, top);
      const intersectionBottom = Math.min(rootRect.bottom, bottom);
      const intersectionHeight = Math.max(0, intersectionBottom - intersectionTop);

      if (axis === "y") {
        return height > 0 && intersectionHeight / height >= ratio;
      }

      const intersectionArea = intersectionWidth * intersectionHeight;
      const elementArea = width * height;

      // Check that at least the specified ratio of the element is visible
      return elementArea > 0 && intersectionArea / elementArea >= ratio;
    }

    const isWithinX = left >= rootRect.left && right <= rootRect.right;
    if (axis === "x") {
      return isWithinX;
    }

    const isWithinY = top >= rootRect.top && bottom <= rootRect.bottom;
    if (axis === "y") {
      return isWithinY;
    }

    return isWithinX && isWithinY;
  });
}

export function getIOSVersion() {
  const { userAgent } = navigator;
  const isIOS = /(iPhone|iPad)/i.test(userAgent);

  if (!isIOS) return null;

  const version = userAgent.match(/OS ([\d_]+)/)?.[1];
  const [major, minor] = version?.split("_") || [];
  if (!version || !major) return null;

  return {
    fullString: version.replace("_", "."),
    major: parseInt(major, 10),
    minor: minor ? parseInt(minor, 10) : 0,
  };
}

/**
 * Determines which grid items should be animated during a transition.
 * It makes an estimation based on the zoom-out card size because it's
 * the common denominator for both transition states. I.e. transitioning either
 * from 10 to 20 cards the other way around, both need 20 cards to be animated.
 * @param {Element} grid - The grid element
 * @param {Element[]} cards - The cards to animate
 * @returns {number} - Number of cards that should be animated
 */
function getCardsToAnimate(grid, cards) {
  if (!grid || !cards || cards.length === 0) return 0;

  const itemSample = cards[0];
  if (!itemSample) return 0;

  // Calculate the visible area of the grid for the Y axis. Assume X is always fully visible:
  const gridRect = grid.getBoundingClientRect();
  const visibleArea = {
    top: Math.max(0, gridRect.top),
    bottom: Math.min(window.innerHeight, gridRect.bottom),
  };

  const visibleHeight = Math.round(visibleArea.bottom - visibleArea.top);
  if (visibleHeight <= 0) return 0;

  /** @type {import('product-card').ProductCard | null} */
  const cardSample = itemSample.querySelector("product-card");
  const gridStyle = getComputedStyle(grid);

  const galleryAspectRatio = cardSample?.refs?.cardGallery?.style.getPropertyValue("--gallery-aspect-ratio") || "";
  let aspectRatio = parseFloat(galleryAspectRatio) || 0.5;
  if (galleryAspectRatio?.includes("/")) {
    const [width = "1", height = "2"] = galleryAspectRatio.split("/");
    aspectRatio = parseInt(width, 10) / parseInt(height, 10);
  }

  const cardGap = parseInt(cardSample?.refs?.productCardLink?.style.getPropertyValue("--product-card-gap") || "") || 12;
  const gridGap = parseInt(gridStyle.getPropertyValue("--product-grid-gap")) || 12;

  // Assume only a couple of lines of text in the card details (title and price).
  // If the title wraps into more lines, we might just animate more cards, but that's fine.
  const detailsSize = ((parseInt(gridStyle.fontSize) || 16) + 2) * 2;

  const isMobile = window.innerWidth < 750;

  // Always use the zoom-out state card width
  const cardWidth = isMobile ? Math.round((gridRect.width - gridGap) / 2) : 100;
  const cardHeight = Math.round(cardWidth / aspectRatio) + cardGap + detailsSize;

  // Calculate the number of cards that fit in the visible area:
  // - The width estimation is pretty accurate, we can ignore decimals.
  // - The height estimation needs to account for peeking rows, so we round up.
  const columnsInGrid = isMobile ? 2 : Math.floor((gridRect.width + gridGap) / (cardWidth + gridGap));
  const rowsInGrid = Math.ceil((visibleHeight - gridGap) / (cardHeight + gridGap));

  return columnsInGrid * rowsInGrid;
}

/**
 * Preloads an image
 * @param {string} src - The source of the image to preload
 */
export function preloadImage(src) {
  const image = new Image();
  image.src = src;
}

export class TextComponent extends HTMLElement {
  shimmer() {
    this.setAttribute("shimmer", "");
  }
}

if (!customElements.get("text-component")) {
  customElements.define("text-component", TextComponent);
}

/**
 * Resets the shimmer attribute on all elements in the container.
 * @param {Element} [container] - The container to reset the shimmer attribute on.
 */
export function resetShimmer(container = document.body) {
  const shimmer = container.querySelectorAll("[shimmer]");
  shimmer.forEach((item) => item.removeAttribute("shimmer"));
}

/**
 * Change the meta theme color of the header.
 * @param {Element} colorSourceElement - The HTML element whose background-color will determine the new theme-color.
 */
export function changeMetaThemeColor(colorSourceElement) {
  const metaThemeColor = document.head.querySelector('meta[name="theme-color"]');
  const containerStyle = window.getComputedStyle(colorSourceElement);
  if (metaThemeColor) metaThemeColor.setAttribute("content", containerStyle.backgroundColor);
}

/**
 * Gets the `view` URL search parameter value, if it exists.
 * Useful for Section Rendering API calls to get HTML markup for the correct template view.
 * Primarily used in testing alternative template views.
 * @returns {string | null} The view parameter value, or null if it doesn't exist
 */
export function getViewParameterValue() {
  return new URLSearchParams(window.location.search).get("view");
}

class Scheduler {
  /** @type {Set<() => void>} */
  #queue = new Set();
  /** @type {boolean} */
  #scheduled = false;

  /** @param {() => void} task */
  schedule = async (task) => {
    this.#queue.add(task);

    if (!this.#scheduled) {
      this.#scheduled = true;

      // Wait for any in-progress view transitions to finish
      if (viewTransition.current) await viewTransition.current;

      requestAnimationFrame(this.flush);
    }
  };

  flush = () => {
    for (const task of this.#queue) {
      task();
    }

    this.#queue.clear();
    this.#scheduled = false;
  };
}

export const scheduler = new Scheduler();

let cartRequest = null;
let cartCache = null;
let cartTimestamp = 0;

export function getCart(options = {}) {
  const { forceRefresh = false, cacheMs = 0 } = options;
  if (cartRequest) {
    return cartRequest;
  }
  if (!forceRefresh && cartCache && cacheMs > 0 && Date.now() - cartTimestamp <= cacheMs) {
    return Promise.resolve(cartCache);
  }
  cartRequest = fetch("/cart.js")
    .then((response) => response.json())
    .then((cart) => {
      cartCache = cart;
      cartTimestamp = Date.now();
      return cart;
    })
    .finally(() => {
      cartRequest = null;
    });
  return cartRequest;
}

/**
 * Cart:update sources dispatched after GoKwik Buy Now checkout is cancelled/restored.
 * PDP listeners must still sync Add to Bag vs quantity for these events.
 * @param {string} [source]
 * @returns {boolean}
 */
export function isBuyNowCartSyncSource(source) {
  return source === "buy-now-restored" || source === "buy-now-settled";
}

/**
 * Whether PDP cart UI should ignore this update because Buy Now checkout is still open.
 * Buy-now restore/settle events must not be skipped — they run while the in-flight flag may still be set.
 * @param {Event & { detail?: { data?: { source?: string } } }} [event]
 * @returns {boolean}
 */
export function shouldSkipBuyNowCartSync(event) {
  if (typeof window === "undefined" || !window._pwBuyNowInFlight) return false;
  return !isBuyNowCartSyncSource(event?.detail?.data?.source);
}

/**
 * Creates a reusable cart message event handler for syncing cart updates from cart drawer.
 * This handles 'quantity_change' events emitted by the cart drawer when quantities are changed or items are deleted.
 *
 * @param {Object} options - Configuration options
 * @param {Function} [options.onQuantityChange] - Callback when quantity changes. Receives { total_cart_quantity, total_cart_amount, id, quantity }
 * @param {string|number|(() => string|number|null)} [options.variantId] - Optional variant ID to filter events for a specific variant. Can be a value or a getter function for dynamic lookup
 * @param {Function} [options.onVariantQuantityChange] - Callback when variant quantity changes. Receives { variantId, quantity }
 * @param {string} [options.logPrefix] - Optional prefix for console logs (for debugging)
 * @returns {{ handler: (event: MessageEvent) => Promise<void>, cleanup: () => void }} Object with handler function and cleanup function
 */
export function createCartMessageHandler({ onQuantityChange, variantId, onVariantQuantityChange, logPrefix = "Cart" } = {}) {
  /** @param {MessageEvent} event */
  const handler = async (event) => {
    try {
      const data = event.data;

      // Early return if not a quantity_change event (ignore other messages like PreviewBar::Ping)
      if (!data || data.name !== "quantity_change" || !data.body) {
        return;
      }

      const { id, quantity, total_cart_quantity, total_cart_amount } = data.body;

      // Get current variant ID (supports both static value and dynamic getter function)
      const currentVariantId = typeof variantId === "function" ? variantId() : variantId;

      // Only log if this is relevant to this component (variant match or total cart update)
      const isRelevant = (currentVariantId && id && currentVariantId.toString() === id.toString()) || (onQuantityChange && typeof total_cart_quantity === "number");

      // if (isRelevant) {
      //   console.log(`${logPrefix} - Quantity change detected:`, {
      //     variantId: currentVariantId,
      //     eventVariantId: id,
      //     quantity,
      //     total_cart_quantity,
      //     total_cart_amount,
      //   });
      // }

      // Handle variant-specific quantity change
      if (currentVariantId && id && currentVariantId.toString() === id.toString()) {
        if (onVariantQuantityChange) {
          await onVariantQuantityChange({ variantId: id, quantity, total_cart_quantity });
        }
      } else if (currentVariantId && !id) {
        // If variant ID is not available in event but we're tracking a variant, fetch cart to sync
        try {
          const cart = await getCart();
          const cartItem = cart.items.find((/** @type {any} */ item) => item.variant_id.toString() === currentVariantId.toString());
          const cartQty = cartItem ? cartItem.quantity : 0;
          if (onVariantQuantityChange) {
            await onVariantQuantityChange({ variantId: currentVariantId, quantity: cartQty });
          }
        } catch (error) {
          console.error(`${logPrefix} - Failed to fetch cart:`, error);
        }
      }

      // Handle total cart quantity change (for cart count updates)
      let effectiveTotal = total_cart_quantity;
      if (typeof effectiveTotal !== "number" && onQuantityChange) {
        // Gokwik/sidecart may send only id + quantity; fetch cart for total
        try {
          const cart = await getCart();
          effectiveTotal = cart.item_count ?? 0;
        } catch (err) {
          // leave effectiveTotal undefined so we skip onQuantityChange
        }
      }
      if (onQuantityChange && typeof effectiveTotal === "number") {
        await onQuantityChange({
          total_cart_quantity: effectiveTotal,
          total_cart_amount: total_cart_amount ?? 0,
          id,
          quantity,
        });
      }
    } catch (error) {
      console.error(`${logPrefix} - Error handling cart message event:`, error);
    }
  };

  const cleanup = () => {
    window.removeEventListener("message", handler);
  };

  // Attach the listener
  window.addEventListener("message", handler);

  return { handler, cleanup };
}

;(function () {
  if (typeof window === "undefined" || typeof document === "undefined") return;

  const selector = ".gfbFrequentBoughtAddToCartButton";
  let initialized = false;

  function isProductPage() {
    const body = document.body;
    if (!body) return false;
    return body.classList.contains("template-product");
  }

  /**
   * Track Fly Bundles "app add to cart" by diffing cart before/after.
   * Uses document click delegation so it works with dynamically injected buttons.
   * @param {HTMLElement} button
   */
  function runFlyBundleAddToBagTracking(button) {
    if (!(button instanceof HTMLElement)) return;

    const now = Date.now();
    const last = Number(button.dataset.flyBundlesLastTrackedAt || "0");
    // Prevent double-firing when both delegation and direct handlers run.
    if (now - last < 2000) return;
    button.dataset.flyBundlesLastTrackedAt = String(now);

    // Prevent overlapping tracking runs for the same button element.
    if (button.dataset.flyBundlesInFlight === "true") return;
    button.dataset.flyBundlesInFlight = "true";

    const offerId = button.getAttribute("data-offer-id") || button.dataset.offerId || null;
    const trackFn = typeof window !== "undefined" ? /** @type {any} */ (window).trackAddToBag : null;

    /** @param {any} props @param {string} key */
    const getProp = (props, key) => {
      if (!props) return undefined;
      if (Array.isArray(props)) {
        const found = props.find((p) => p?.name === key || p?.key === key);
        return found?.value;
      }
      return /** @type {any} */ (props)[key];
    };

    /** @param {any} it */
    const parseProductSlug = (it) => {
      const handle = it?.product_handle || it?.productHandle || it?.handle || null;
      if (handle) return String(handle);
      const url = it?.url || it?.product_url || null;
      if (typeof url === "string") {
        const m = url.match(/\/products\/([^\/\?\#]+)/);
        return m ? m[1] : null;
      }
      return null;
    };

    (async () => {
      try {
        // Snapshot cart before bundle is applied so we can detect newly added bundle items.
        let cartBefore = null;
        try {
          cartBefore = await getCart({ forceRefresh: true });
        } catch (e) {
          cartBefore = null;
        }

        /** @type {Record<string, number>} */
        const qtyByVariantBefore = {};
        if (cartBefore && Array.isArray(cartBefore.items)) {
          for (const it of cartBefore.items) {
            const vid = it?.variant_id != null ? String(it.variant_id) : null;
            if (!vid) continue;
            qtyByVariantBefore[vid] = typeof it.quantity === "number" ? it.quantity : 0;
          }
        }

        /**
         * Poll only a few times to avoid hammering `/cart.js`.
         * Stop as soon as we detect any newly added items.
         */
        const pollDelaysMs = [900, 1500, 2500];
        let cart = null;
        let deltaItems = /** @type {{ it: any, variantId: string, deltaQty: number }[]} */ ([]);

        for (const delayMs of pollDelaysMs) {
          await new Promise((r) => setTimeout(r, delayMs));
          cart = await getCart({ forceRefresh: true });
          const items = Array.isArray(cart?.items) ? cart.items : [];

          // Detect deltas (new quantities for variants).
          deltaItems = [];
          for (const it of items) {
            const variantId = it?.variant_id != null ? String(it.variant_id) : null;
            if (!variantId) continue;
            const beforeQty = qtyByVariantBefore[variantId] || 0;
            const afterQty = typeof it.quantity === "number" ? it.quantity : 0;
            const deltaQty = afterQty - beforeQty;
            if (deltaQty <= 0) continue;
            deltaItems.push({ it, variantId, deltaQty });
          }

          if (deltaItems.length > 0) break;
        }

        if (!cart || deltaItems.length === 0) return;

        const itemCount = typeof cart?.item_count === "number" ? cart.item_count : 0;

        let finalItems = deltaItems;
        // If OfferId is present in cart item properties, filter by it.
        if (offerId) {
          const matched = deltaItems.filter(({ it }) => {
            const props = it?.properties || it?.line_properties || null;
            const cartOfferId =
              getProp(props, "_flyBundles:OfferId") || getProp(props, "OfferId") || "";
            return String(cartOfferId).indexOf(String(offerId)) === 0;
          });
          // If we can't find the OfferId in properties, fall back to all delta items.
          if (matched.length > 0) finalItems = matched;
        }

        const storefrontToken =
          (typeof window !== "undefined" &&
            (window.PW_STOREFRONT_TOKEN || window.__PW_STOREFRONT_TOKEN__)) ||
          "";
        if (
          window.PWCartLineItemProps &&
          typeof window.PWCartLineItemProps.patchMissingProductProps === "function" &&
          storefrontToken
        ) {
          try {
            await window.PWCartLineItemProps.patchMissingProductProps(
              finalItems.map(({ it }) => it),
              storefrontToken
            );
          } catch (patchErr) {
            console.error("Fly Bundles product props patch failed:", patchErr);
          }
        }

        if (typeof trackFn === "function") {
          for (const { it, variantId, deltaQty } of finalItems) {
            const productId = it?.product_id != null ? String(it.product_id) : null;
            const productTitle = it?.product_title || it?.title || null;
            const productSlug = parseProductSlug(it);
            const price = it?.price != null ? Number(it.price) : null;

            trackFn({
              cta_name: "add_to_bag",
              total_quantity: deltaQty,
              product_id: productId,
              product_title: productTitle,
              product_slug: productSlug,
              product_price: price,
              product_discounted_price: price,
              product_variant_id: variantId,
              triggerElement: button,
            });
          }
        }

        document.dispatchEvent(
          new CustomEvent("cart:update", {
            detail: {
              data: {
                itemCount,
                source: "fly-bundles",
              },
            },
          })
        );
      } catch (error) {
        console.error("Fly Bundles add-to-cart tracking failed:", error);
      } finally {
        button.dataset.flyBundlesInFlight = "false";
      }
    })();
  }

  /** @param {HTMLElement} button */
  function attach(button) {
    if (!(button instanceof HTMLElement)) return;
    if (button.dataset.flyBundlesHooked === "true") return;
    button.dataset.flyBundlesHooked = "true";
    button.addEventListener("click", () => runFlyBundleAddToBagTracking(button));
  }

  function scan() {
    const buttons = document.querySelectorAll(selector);
    buttons.forEach(attach);
  }

  function init() {
    if (initialized) return;
    initialized = true;

    scan();

    const observer = new MutationObserver(() => {
      scan();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    // Delegated click handler (handles app-injected buttons + shadow DOM)
    document.addEventListener(
      "click",
      (event) => {
        const path = event?.composedPath?.() || [];
        for (const el of path) {
          if (!(el instanceof HTMLElement)) continue;
          if (!el.matches(selector)) continue;
          runFlyBundleAddToBagTracking(el);
          break;
        }
      },
      true
    );
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init, { once: true });
  } else {
    init();
  }
})();

Theme.utilities = {
  ...Theme.utilities,
  scheduler: scheduler,
  createCartMessageHandler: createCartMessageHandler,
  getCart: getCart,
};
